import { createPortal } from "react-dom";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useHoverSupport from "../../../hooks/useHoverSupport";
import { Spinner } from "../Spinner";

const DEFAULT_OFFSET = 16;

// The zoom preview targets a fixed fraction of the viewport *area*, so portrait
// and landscape images render at the same size. It is then clamped so it can
// never grow larger than these fractions of the viewport. Tweak to resize.
const TARGET_AREA_FRACTION = 0.08;
const MAX_WIDTH_FRACTION = 0.45;
const MAX_HEIGHT_FRACTION = 0.6;

const ensurePositiveNumber = value =>
  Number.isFinite(value) && value > 0 ? value : 1;

const clampValue = (value, min, max) =>
  Math.min(Math.max(value, min), Math.max(min, max));

const computeZoomDimensions = (
  naturalWidth,
  naturalHeight,
  viewportWidth,
  viewportHeight,
) => {
  const targetArea = viewportWidth * viewportHeight * TARGET_AREA_FRACTION;
  const aspectRatio =
    naturalWidth && naturalHeight ? naturalWidth / naturalHeight : 1;

  const idealWidth = Math.sqrt(targetArea * aspectRatio);
  const idealHeight = Math.sqrt(targetArea / aspectRatio);

  // Shrink (preserving aspect ratio) only if the ideal box would overflow the
  // viewport; this keeps the equal-area guarantee for all normal aspect ratios.
  const fitScale = Math.min(
    1,
    (viewportWidth * MAX_WIDTH_FRACTION) / idealWidth,
    (viewportHeight * MAX_HEIGHT_FRACTION) / idealHeight,
  );

  return {
    width: Math.max(Math.round(idealWidth * fitScale), 1),
    height: Math.max(Math.round(idealHeight * fitScale), 1),
  };
};

const computeAnchoredPosition = (rect, size, viewport, offset) => {
  const { width, height } = size;
  const { innerWidth, innerHeight } = viewport;

  const centeredTop = clampValue(
    rect.top + rect.height / 2 - height / 2,
    offset,
    innerHeight - offset - height,
  );
  const centeredLeft = clampValue(
    rect.left + rect.width / 2 - width / 2,
    offset,
    innerWidth - offset - width,
  );

  const placements = [
    {
      fits: innerWidth - rect.right - offset >= width,
      space: innerWidth - rect.right - offset,
      top: centeredTop,
      left: rect.right + offset,
    },
    {
      fits: rect.left - offset >= width,
      space: rect.left - offset,
      top: centeredTop,
      left: rect.left - offset - width,
    },
    {
      fits: innerHeight - rect.bottom - offset >= height,
      space: innerHeight - rect.bottom - offset,
      top: rect.bottom + offset,
      left: centeredLeft,
    },
    {
      fits: rect.top - offset >= height,
      space: rect.top - offset,
      top: rect.top - offset - height,
      left: centeredLeft,
    },
  ];

  const chosen =
    placements.find(placement => placement.fits) ||
    placements.reduce((best, placement) =>
      placement.space > best.space ? placement : best,
    );

  return {
    top: clampValue(chosen.top, offset, innerHeight - offset - height),
    left: clampValue(chosen.left, offset, innerWidth - offset - width),
  };
};

const resolveOffset = value =>
  Number.isFinite(value) ? Math.max(value, 0) : DEFAULT_OFFSET;

const getScaledDimensions = (
  naturalWidth,
  naturalHeight,
  maxWidth,
  maxHeight,
) => {
  const safeMaxWidth = ensurePositiveNumber(maxWidth);
  const safeMaxHeight = ensurePositiveNumber(maxHeight);

  if (!naturalWidth || !naturalHeight) {
    return { width: safeMaxWidth, height: safeMaxHeight };
  }

  const widthScale = safeMaxWidth / naturalWidth;
  const heightScale = safeMaxHeight / naturalHeight;
  const scale = Math.min(widthScale, heightScale, 1);

  return {
    width: Math.max(Math.round(naturalWidth * scale), 1),
    height: Math.max(Math.round(naturalHeight * scale), 1),
  };
};

const ImageZoom = props => {
  const {
    alt = "Preview image",
    className = "",
    offset = DEFAULT_OFFSET,
    onClick,
    src,
    zoomSrc,
    onImageError,
    onImageLoad,
  } = props || {};

  const safeOffset = useMemo(() => resolveOffset(offset), [offset]);

  const [imageDimensions, setImageDimensions] = useState({ width: null, height: null });

  const calculateZoomDimensions = useCallback(
    (naturalWidth, naturalHeight) =>
      computeZoomDimensions(
        naturalWidth,
        naturalHeight,
        window.innerWidth,
        window.innerHeight,
      ),
    [],
  );

  const [resolvedZoomDimensions, setResolvedZoomDimensions] = useState(() =>
    calculateZoomDimensions(null, null)
  );

  const { width: maxZoomWidth, height: maxZoomHeight } = resolvedZoomDimensions;

  const [isVisible, setIsVisible] = useState(false);
  const [zoomSize, setZoomSize] = useState(resolvedZoomDimensions);
  const [position, setPosition] = useState(() => ({
    top: safeOffset,
    left: safeOffset,
    width: maxZoomWidth,
    height: maxZoomHeight,
  }));
  const [hasError, setHasError] = useState(false);
  const [isZoomImageLoading, setIsZoomImageLoading] = useState(false);
  const [mountNode, setMountNode] = useState(null);
  const isHoverSupported = useHoverSupport();

  const resolvedZoomSrc = zoomSrc || src;

  const anchorRectRef = useRef(null);

  const applyAnchoredPosition = useCallback(
    (rect, size) => {
      if (!rect) {
        return;
      }

      const next = computeAnchoredPosition(
        rect,
        size,
        { innerWidth: window.innerWidth, innerHeight: window.innerHeight },
        safeOffset,
      );

      setPosition({
        top: next.top,
        left: next.left,
        width: size.width,
        height: size.height,
      });
    },
    [safeOffset],
  );

  useEffect(() => {
    setMountNode(document.body);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setResolvedZoomDimensions(calculateZoomDimensions(imageDimensions.width, imageDimensions.height));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateZoomDimensions, imageDimensions]);

  // Update resolvedZoomDimensions when imageDimensions change
  useEffect(() => {
    if (imageDimensions.width && imageDimensions.height) {
      setResolvedZoomDimensions(calculateZoomDimensions(imageDimensions.width, imageDimensions.height));
    }
  }, [imageDimensions, calculateZoomDimensions]);

  useEffect(() => {
    // Only update zoomSize if we have image dimensions to calculate proper aspect ratio
    // Otherwise, let handleZoomImageLoad set it when the image loads
    if (imageDimensions.width && imageDimensions.height) {
      const size = getScaledDimensions(
        imageDimensions.width,
        imageDimensions.height,
        maxZoomWidth,
        maxZoomHeight,
      );

      setZoomSize(prev =>
        prev.width === size.width && prev.height === size.height ? prev : size,
      );

      applyAnchoredPosition(anchorRectRef.current, size);
    } else {
      applyAnchoredPosition(anchorRectRef.current, {
        width: maxZoomWidth,
        height: maxZoomHeight,
      });
    }

    setHasError(false);
    if (isVisible && resolvedZoomSrc) {
      setIsZoomImageLoading(true);
    }
  }, [
    maxZoomHeight,
    maxZoomWidth,
    resolvedZoomSrc,
    isVisible,
    imageDimensions,
    applyAnchoredPosition,
  ]);

  useEffect(() => {
    if (!resolvedZoomSrc || !isHoverSupported) {
      setIsVisible(false);
    }
  }, [resolvedZoomSrc, isHoverSupported]);

  const [isHovering, setIsHovering] = useState(false);
  const handleMouseEnter = useCallback(
    event => {
      // Prevent duplicate hover events
      if (isHovering) {
        return;
      }
      setIsHovering(true);
      if (!resolvedZoomSrc || !isHoverSupported) return;

      const allZoomElements = document.querySelectorAll('[data-image-zoom-element]');
      allZoomElements.forEach(element => {
        element.style.display = 'none';
      });

      const rect = event.currentTarget.getBoundingClientRect();
      anchorRectRef.current = rect;
      applyAnchoredPosition(rect, zoomSize);
      setIsVisible(true);
      setIsZoomImageLoading(true);
      setHasError(false);
    },
    [resolvedZoomSrc, applyAnchoredPosition, isHoverSupported, isHovering, zoomSize],
  );

  const handleMouseLeave = useCallback(() => {
    anchorRectRef.current = null;
    setIsHovering(false);
    setIsVisible(false);
    setIsZoomImageLoading(false);
  }, []);

  const handleClick = useCallback(
    event => {
      setIsHovering(false);
      if (typeof onClick === "function") {
        onClick(event);
        return;
      }
    },
    [onClick],
  );

  const handleImageError = useCallback(
    event => {
      setHasError(true);
      onImageError?.(event);
    },
    [onImageError],
  );

  const handleImageLoad = useCallback(
    event => {
      const { naturalWidth, naturalHeight } = event?.currentTarget || {};
      if (naturalWidth && naturalHeight) {
        setImageDimensions({ width: naturalWidth, height: naturalHeight });
      }
      setHasError(false);
      onImageLoad?.(event);
    },
    [onImageLoad],
  );

  const handleZoomImageLoad = useCallback(
    event => {
      const { naturalWidth, naturalHeight } = event?.currentTarget || {};
      
      // Update image dimensions if not already set or if zoom image has different dimensions
      if (naturalWidth && naturalHeight) {
        setImageDimensions(prev => {
          // Only update if dimensions changed
          if (prev.width !== naturalWidth || prev.height !== naturalHeight) {
            return { width: naturalWidth, height: naturalHeight };
          }
          return prev;
        });
      }
      
      const size = getScaledDimensions(
        naturalWidth,
        naturalHeight,
        maxZoomWidth,
        maxZoomHeight,
      );

      setZoomSize(prev =>
        prev.width === size.width && prev.height === size.height ? prev : size,
      );

      applyAnchoredPosition(anchorRectRef.current, size);

      setHasError(false);
      setIsZoomImageLoading(false);
    },
    [maxZoomHeight, maxZoomWidth, applyAnchoredPosition],
  );

  const handleZoomImageError = useCallback(
    event => {
      setHasError(true);
      setIsZoomImageLoading(false);
      onImageError?.(event);
    },
    [onImageError],
  );

  useEffect(() => {
    if (!resolvedZoomSrc) {
      setHasError(false);
      setIsZoomImageLoading(false);
    }
  }, [resolvedZoomSrc]);

  // Preload zoom image (hidden) when hover starts
  const zoomImagePreloader = useMemo(() => {
    if (!isHoverSupported || !isVisible || !resolvedZoomSrc || hasError || !isZoomImageLoading) {
      return null;
    }

    return (
      <img
        alt=""
        className="hidden"
        src={resolvedZoomSrc}
        onError={handleZoomImageError}
        onLoad={handleZoomImageLoad}
      />
    );
  }, [
    hasError,
    isVisible,
    isZoomImageLoading,
    resolvedZoomSrc,
    handleZoomImageError,
    handleZoomImageLoad,
    isHoverSupported,
  ]);

  const zoomElement = useMemo(() => {
    if (!isHoverSupported || !isVisible || !mountNode || !resolvedZoomSrc || hasError || isZoomImageLoading) {
      return null;
    }

    const style = {
      top: position.top,
      left: position.left,
      width: position.width,
      height: position.height,
    };

    return createPortal(
      <div
        data-image-zoom-element
        className="pointer-events-none fixed z-[60] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-2xl"
        style={{
          ...style,
          animation: "fadeIn 0.2s ease-in-out forwards",
        }}
      >
        <img
          alt={alt}
          className="h-full w-full object-contain"
          src={resolvedZoomSrc}
          onError={handleZoomImageError}
          onLoad={handleZoomImageLoad}
        />
      </div>,
      mountNode,
    );
  }, [
    alt,
    hasError,
    isVisible,
    isZoomImageLoading,
    mountNode,
    position,
    resolvedZoomSrc,
    handleZoomImageError,
    handleZoomImageLoad,
  ]);

  if (!src) {
    return null;
  }

  const cursorClass = onClick ? "cursor-pointer" : isHoverSupported ? "cursor-zoom-in" : "cursor-pointer";

  return (
    <div
      className={`relative inline-flex h-full w-full ${cursorClass} items-center justify-center ${className}`}
      onClick={handleClick}
      onMouseEnter={isHoverSupported ? handleMouseEnter : undefined}
      onMouseLeave={isHoverSupported ? handleMouseLeave : undefined}
      role="button"
      tabIndex={0}
      onKeyDown={event => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick(event);
        }
      }}
      aria-label={alt}
    >
      <div className="relative h-full w-full">
        <img
          alt={alt}
          className="h-full w-full object-cover"
          src={src}
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
        {isZoomImageLoading && isVisible && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <Spinner className="!m-0 h-6 w-6 text-gray-400" />
          </div>
        )}
      </div>
      {zoomImagePreloader}
      {zoomElement}
    </div>
  );
};

export {
  ImageZoom,
  computeZoomDimensions,
  computeAnchoredPosition,
  TARGET_AREA_FRACTION,
  MAX_WIDTH_FRACTION,
  MAX_HEIGHT_FRACTION,
};

